import React from "react";
import { shallowEqual } from "react-redux";
import BasketReclaimableVatDisclaimer from "../content/BasketReclaimableVatDisclaimer";
import { getBasketCtas } from "../../../../../utils/ctaUtils";
import useNavParams from "../../../../../hooks/useNavParams";
import * as Styles from "./UscBasketCtas.styles";
import Button from "../../../../../../shared-components/toyota/styles/atoms/Button";
import { UscTrackingLocation } from "../../../../../utils/tracking";
import PdfButton from "./PdfButton";
import { useUscSelector } from "../../../../../redux/store";
import { showUscPriceSelector } from "../../../../../utils/priceUtils";
import SaveToMyToyotaButton from "./SaveToMyToyotaButton";
import { getToyotaButtonVariant } from "../../../../../utils/pdpUtils";
import Icon from "../../../../../../shared-components/toyota/styles/atoms/Icon";
import { useUscLabel } from "../../../../../utils/labels";
import { VEHICLE_AGGREGATION_THRESHOLD } from "../../../../../../shared-logic/utils/uscUtils";

const UscBasketCtas = ({ trackingLocation }: { trackingLocation: UscTrackingLocation }): JSX.Element => {
    const navParams = useNavParams(trackingLocation);
    const ctas = getBasketCtas(navParams);
    const showPrice = useUscSelector(showUscPriceSelector, shallowEqual);
    const vehicleAggregationsEnabled = useUscSelector((state) => state.uscSettings.enableVehicleAggregations);
    const hasFewVehicles =
        useUscSelector((state) => state.vehicle.result?.vehicleCount ?? 0) < VEHICLE_AGGREGATION_THRESHOLD;

    const { context } = navParams;

    const showBasketCtas = ctas.primary || !!ctas.secondary.length || !showPrice || vehicleAggregationsEnabled;

    const vehicleAggregationCountLabel = useUscLabel(
        hasFewVehicles ? "uscVehicleAggregationLess" : "uscVehicleAggregationMore",
    );
    const vehicleAggregationLabel = useUscLabel("uscVehicleAggregationCarsAvailable");

    return (
        <>
            {showBasketCtas && (
                <Styles.Wrapper>
                    {vehicleAggregationsEnabled && (
                        <Styles.VehicleAggregation>
                            <Icon
                                variant={
                                    hasFewVehicles
                                        ? "vehicle-aggregations-fewer-cars"
                                        : "vehicle-aggregations-multiple-cars"
                                }
                            />
                            <Styles.VehicleAggregationText>
                                <span>{vehicleAggregationCountLabel}</span>
                                {vehicleAggregationLabel}
                            </Styles.VehicleAggregationText>
                        </Styles.VehicleAggregation>
                    )}
                    {ctas.primary && (
                        <Button
                            variant={getToyotaButtonVariant(context)}
                            href={ctas.primary.href || "#"}
                            onClick={ctas.primary.onClick}
                            rel={ctas.primary.rel}
                        >
                            {ctas.primary.label}
                        </Button>
                    )}

                    {ctas.secondary.map((secondaryCta, index) => {
                        return (
                            <Button
                                variant="outline"
                                /* eslint-disable-next-line react/no-array-index-key */
                                key={index}
                                href={secondaryCta.href || "#"}
                                onClick={secondaryCta.onClick}
                                rel={secondaryCta.rel}
                            >
                                {secondaryCta.label}
                            </Button>
                        );
                    })}

                    {/*
                    The PDF and SaveToMyToyota buttons are normally shown inside the Basket Details modal.
                    The Basket Details modal is hidden when a vehicle has no prices. In this case we show these buttons in the basket
                */}
                    {!showPrice && (
                        <Styles.SecondaryCtas>
                            <PdfButton buttonVariant="basket-cta" trackingLocation={UscTrackingLocation.Basket} />
                            <SaveToMyToyotaButton
                                buttonVariant="basket-cta"
                                trackingLocation={UscTrackingLocation.Basket}
                            />
                        </Styles.SecondaryCtas>
                    )}
                </Styles.Wrapper>
            )}
            <BasketReclaimableVatDisclaimer />
        </>
    );
};

export default UscBasketCtas;
